/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The live Stripe subscription status
 */
export const StripeSubscriptionStatus = {
  Incomplete: "incomplete",
  IncompleteExpired: "incomplete_expired",
  Trialing: "trialing",
  Active: "active",
  PastDue: "past_due",
  Canceled: "canceled",
  Unpaid: "unpaid",
  Paused: "paused",
} as const;
/**
 * The live Stripe subscription status
 */
export type StripeSubscriptionStatus = OpenEnum<
  typeof StripeSubscriptionStatus
>;

export type StripeSubscription = {
  /**
   * Scheduled cancellation time, when present
   */
  cancelAt?: Date | undefined;
  /**
   * Whether Stripe will cancel the subscription at the end of the current period
   */
  cancelAtPeriodEnd: boolean;
  /**
   * Time cancellation was requested or completed, when present
   */
  canceledAt?: Date | undefined;
  /**
   * End of the current Stripe service period (exclusive)
   */
  currentPeriodEnd: Date;
  /**
   * Start of the current Stripe service period
   */
  currentPeriodStart: Date;
  /**
   * Verified terms of a currently applied Stripe subscription discount; absent when unsupported or inactive
   */
  discountDescription?: string | undefined;
  /**
   * Discount expiration reported by Stripe, when present
   */
  discountEndsAt?: Date | undefined;
  /**
   * Whether the live discount and gateway price verify the monthly five GiB promotion
   */
  gatewayPromotionApplied?: boolean | undefined;
  /**
   * Whether a past-due subscription has an open latest invoice with an unpaid balance
   */
  paymentFailed: boolean;
  /**
   * The live Stripe subscription status
   */
  status: StripeSubscriptionStatus;
  /**
   * End of the Stripe trial, when one is configured
   */
  trialEnd?: Date | undefined;
  /**
   * Start of the Stripe trial, when the subscription is trialing
   */
  trialStart?: Date | undefined;
};

/** @internal */
export const StripeSubscriptionStatus$inboundSchema: z.ZodMiniType<
  StripeSubscriptionStatus,
  unknown
> = openEnums.inboundSchema(StripeSubscriptionStatus);

/** @internal */
export const StripeSubscription$inboundSchema: z.ZodMiniType<
  StripeSubscription,
  unknown
> = z.pipe(
  z.object({
    cancel_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    cancel_at_period_end: z.boolean(),
    canceled_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    current_period_end: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    current_period_start: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    discount_description: z.optional(z.string()),
    discount_ends_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    gateway_promotion_applied: z.optional(z.boolean()),
    payment_failed: z.boolean(),
    status: StripeSubscriptionStatus$inboundSchema,
    trial_end: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    trial_start: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "cancel_at": "cancelAt",
      "cancel_at_period_end": "cancelAtPeriodEnd",
      "canceled_at": "canceledAt",
      "current_period_end": "currentPeriodEnd",
      "current_period_start": "currentPeriodStart",
      "discount_description": "discountDescription",
      "discount_ends_at": "discountEndsAt",
      "gateway_promotion_applied": "gatewayPromotionApplied",
      "payment_failed": "paymentFailed",
      "trial_end": "trialEnd",
      "trial_start": "trialStart",
    });
  }),
);

export function stripeSubscriptionFromJSON(
  jsonString: string,
): SafeParseResult<StripeSubscription, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => StripeSubscription$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'StripeSubscription' from JSON`,
  );
}
